'use client';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

export interface ScoreNoteStep {
  /** Position index in the flattened, monophonic step list. */
  index: number;
  /** MIDI note number (e.g. A4 = 69). null for rests. */
  midi: number | null;
  /** Short pitch label such as "A4", "F#5", or "rest". */
  label: string;
  /** Duration in whole notes (1 = whole, 0.25 = quarter). */
  durationWhole: number;
  /** Absolute timestamp from start of piece, in whole notes. */
  timestampWhole: number;
  /** Reference back to the OSMD note so we can color it. */
  noteRef: any;
}

export interface ScoreViewerHandle {
  showCursor: () => void;
  hideCursor: () => void;
  goTo: (stepIndex: number) => void;
  markStep: (stepIndex: number, status: 'correct' | 'wrong' | 'reset') => void;
  resetColors: () => void;
}

interface Props {
  /** Either a URL path (public/) or a File from an <input type="file">. */
  source: string | File | null;
  onReady?: (steps: ScoreNoteStep[]) => void;
  onError?: (err: string) => void;
  onLoadingChange?: (loading: boolean) => void;
}

// OSMD stores octave internally as (xmlOctave − 3), so octXmlDiff=3.
// ToStringShort(3) restores the standard scientific pitch notation (A4, C4…).
const OSMD_OCTAVE_OFFSET = 3;

function pitchToLabel(pitch: any): string {
  if (!pitch) return 'rest';
  try {
    return pitch.ToStringShort(OSMD_OCTAVE_OFFSET);
  } catch {
    return 'rest';
  }
}

function pitchToMidi(pitch: any): number | null {
  if (!pitch) return null;
  const label = pitchToLabel(pitch);
  const m = label.match(/^([A-Ga-g])([#b]?)(-?\d+)$/);
  if (!m) return null;
  const [, letterRaw, accidental, octStr] = m;
  const letter = letterRaw.toUpperCase();
  const letterSemis: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  const base = letterSemis[letter];
  if (base === undefined) return null;
  const acc = accidental === '#' ? 1 : accidental === 'b' ? -1 : 0;
  const oct = parseInt(octStr, 10);
  return (oct + 1) * 12 + base + acc;
}

export const ScoreViewer = forwardRef<ScoreViewerHandle, Props>(function ScoreViewer(
  { source, onReady, onError, onLoadingChange },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const osmdRef = useRef<any>(null);
  const stepsRef = useRef<ScoreNoteStep[]>([]);
  const cursorObserverRef = useRef<MutationObserver | null>(null);
  const [, force] = useState(0);

  useImperativeHandle(ref, () => ({
    showCursor: () => {
      const cursor = osmdRef.current?.cursor;
      if (!cursor) return;
      cursor.show();
      cursor.update();
    },
    hideCursor: () => osmdRef.current?.cursor?.hide(),
    goTo: (idx: number) => {
      const osmd = osmdRef.current;
      if (!osmd?.cursor) return;
      osmd.cursor.reset();
      for (let i = 0; i < idx; i++) osmd.cursor.next();
      // update() repositions the cursor element visually after moving.
      osmd.cursor.update();
      osmd.cursor.show();
    },
    markStep: (idx: number, status: 'correct' | 'wrong' | 'reset') => {
      const osmd = osmdRef.current;
      const step = stepsRef.current[idx];
      if (!osmd?.rules || !step?.noteRef) return;
      const color =
        status === 'correct' ? '#22c55e' : status === 'wrong' ? '#ef4444' : '#000000';
      const gNote = osmd.rules.GNote(step.noteRef);
      if (gNote?.setColor) {
        gNote.setColor(color, { applyToNoteheads: true, applyToStem: true });
      }
    },
    resetColors: () => {
      const osmd = osmdRef.current;
      if (!osmd?.rules) return;
      for (const step of stepsRef.current) {
        const gNote = osmd.rules.GNote(step.noteRef);
        if (gNote?.setColor) {
          gNote.setColor('#000000', { applyToNoteheads: true, applyToStem: true });
        }
      }
    },
  }));

  useEffect(() => {
    if (!containerRef.current || !source) return;
    let cancelled = false;

    async function loadScore() {
      onLoadingChange?.(true);
      try {
        const { OpenSheetMusicDisplay } = await import('opensheetmusicdisplay');
        if (cancelled || !containerRef.current) return;

        if (!osmdRef.current) {
          osmdRef.current = new OpenSheetMusicDisplay(containerRef.current, {
            backend: 'svg',
            drawingParameters: 'compacttight',
            autoResize: true,
            // Disable auto-scrolling to the cursor — otherwise the page jumps
            // to the top of the score on every cursor.next().
            followCursor: false,
            // Use OSMD's default cursor (type 0: standard highlight over
            // current notes — the familiar translucent yellow block).
          });
        }

        const osmd = osmdRef.current;

        let content: string | Blob | Document;
        if (typeof source === 'string') {
          content = source;
        } else {
          content = source as Blob;
        }

        await osmd.load(content);
        if (cancelled) return;
        osmd.render();

        // init() must be called before show()/next()/reset() — enableOrDisableCursors
        // is the public entry point that triggers Cursor.init() internally.
        osmd.enableOrDisableCursors(true);

        // OSMD sets the cursor img's size via the HTML width/height attributes
        // (`r.height = ...`, `r.width = ...`). Tailwind preflight's
        // `img { height: auto; max-width: 100% }` overrides those, collapsing
        // the cursor to ~1px (the canvas data-URL is 1px tall and stretched).
        // Mirror the attributes to inline styles so they win the cascade.
        cursorObserverRef.current?.disconnect();
        const cursorEl: HTMLImageElement | undefined = osmd.cursor?.cursorElement;
        if (cursorEl) {
          const sync = () => {
            const w = cursorEl.getAttribute('width');
            const h = cursorEl.getAttribute('height');
            if (w) cursorEl.style.width = `${w}px`;
            if (h) cursorEl.style.height = `${h}px`;
          };
          sync();
          const obs = new MutationObserver(sync);
          obs.observe(cursorEl, { attributes: true, attributeFilter: ['width', 'height'] });
          cursorObserverRef.current = obs;
        }

        const steps: ScoreNoteStep[] = [];
        const iter = osmd.Sheet.MusicPartManager.getIterator();
        let idx = 0;

        while (!iter.EndReached) {
          const entries = iter.CurrentVoiceEntries;
          if (entries && entries.length > 0) {
            const primaryNote = entries[0].Notes[0];
            if (primaryNote) {
              const pitch = primaryNote.Pitch;
              const lenFrac = primaryNote.Length;
              const tsFrac = iter.CurrentSourceTimestamp;
              steps.push({
                index: idx++,
                midi: pitchToMidi(pitch),
                label: pitch ? pitchToLabel(pitch) : 'rest',
                durationWhole: lenFrac?.RealValue ?? 0.25,
                timestampWhole: tsFrac?.RealValue ?? 0,
                noteRef: primaryNote,
              });
            }
          }
          iter.moveToNext();
        }

        stepsRef.current = steps;
        osmd.cursor.reset();
        osmd.cursor.hide();
        onReady?.(steps);
        force((v) => v + 1);
      } catch (e: any) {
        if (!cancelled) onError?.(e?.message ?? String(e));
      } finally {
        if (!cancelled) onLoadingChange?.(false);
      }
    }

    loadScore();
    return () => {
      cancelled = true;
      cursorObserverRef.current?.disconnect();
      cursorObserverRef.current = null;
    };
  }, [source, onReady, onError, onLoadingChange]);

  return (
    <div
      ref={containerRef}
      // `relative` is required so the cursor img (absolutely positioned by
      // OSMD) is anchored to this container instead of escaping to the page.
      className="osmd-container relative isolate w-full bg-white rounded-xl p-4 min-h-[160px]"
    />
  );
});
